import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { WEBSOCKET_CONFIG, CONNECTION_STATES } from '../constants/webSocketConstants';

/**
 * Low-level WebSocket connection management using STOMP over SockJS
 * 
 * This class handles:
 * - Connection establishment and teardown
 * - Automatic reconnection with exponential backoff
 * - Connection state management
 * - Basic subscription management
 * - Authentication handling
 */
class WebSocketManager {
  constructor() {
    this.client = null;
    this.connectionState = CONNECTION_STATES.DISCONNECTED;
    this.reconnectAttempts = 0;
    this.subscriptions = new Map(); // subscriptionId -> subscription object
    this.messageHandlers = new Map(); // destination -> Set of handlers
    this.connectionListeners = new Set();
    this.reconnectTimeoutId = null;
    this.isManualDisconnect = false;
    this.wantConnected = false; // true after a successful connect until disconnect() is called

    // A dropped network or a sleeping device closes the socket without a clean disconnect, and the retries
    // below may have given up by the time it is back, so try again as soon as the browser says so.
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.recoverConnection());
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.recoverConnection();
        }
      });
    }
  }

  /**
   * Reconnect now if the connection was lost and nothing is already connecting
   * @private
   */
  recoverConnection() {
    if (!this.wantConnected || this.isManualDisconnect || this.isConnected()
        || this.connectionState === CONNECTION_STATES.CONNECTING) {
      return;
    }
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    this.reconnectAttempts = 0;
    this.attemptReconnect();
  }

  /**
   * Initialize and connect to WebSocket server
   * @param {string|null} authToken - JWT authentication token (can be null for cookie-based auth)
   * @param {Object} options - Connection options
   */
  connect(authToken, options = {}) {
    if (this.client && this.client.connected) {
      console.warn('⚠️ WebSocket already connected');
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        // A client left over from a lost connection is already closed; make sure it cannot reconnect or fire again
        if (this.client) {
          this.client.deactivate();
        }
        let established = false;
        const wsUrl = this.getWebSocketUrl();
        
        // Append token as query param in development (cookies don't work cross-port)
        // Production uses cookie-based auth via ALB path-based routing
        const wsUrlWithAuth = authToken ? `${wsUrl}?token=${authToken}` : wsUrl;
        
        // Create STOMP client with SockJS transport
        const client = new Client({
          // Use SockJS for better compatibility
          webSocketFactory: () => new SockJS(wsUrlWithAuth),

          // Authentication: query param (dev) or cookie (prod)
          connectHeaders: {
            'X-Requested-With': 'XMLHttpRequest'
          },

          // Heartbeat configuration for connection health
          heartbeatIncoming: WEBSOCKET_CONFIG.HEARTBEAT.INCOMING,
          heartbeatOutgoing: WEBSOCKET_CONFIG.HEARTBEAT.OUTGOING,

          // Reconnection is handled manually for better control
          reconnectDelay: 0,

          // Connection event handlers
          onConnect: (frame) => {
            established = true;
            this.wantConnected = true;
            this.connectionState = CONNECTION_STATES.CONNECTED;
            this.reconnectAttempts = 0;
            this.isManualDisconnect = false;
            this.notifyConnectionListeners();
            resolve(frame);
          },

          onDisconnect: (frame) => {
            this.connectionState = CONNECTION_STATES.DISCONNECTED;
            this.notifyConnectionListeners();
            
            // Only attempt reconnection if not manually disconnected
            if (!this.isManualDisconnect) {
              this.scheduleReconnect();
            }
          },

          onStompError: (frame) => {
            console.error('❌ STOMP error:', frame);
            this.connectionState = CONNECTION_STATES.ERROR;
            this.notifyConnectionListeners();
            reject(new Error(`STOMP error: ${frame.headers['message']}`));
          },

          onWebSocketError: (event) => {
            console.error('❌ WebSocket error:', event);
            this.connectionState = CONNECTION_STATES.ERROR;
            this.notifyConnectionListeners();
          },

          // The socket closed. A clean disconnect is handled by onDisconnect; this is a lost connection or a
          // connection attempt that never opened.
          onWebSocketClose: () => {
            if (this.client !== client) {
              return;
            }
            if (!established) {
              reject(new Error('WebSocket closed before it connected'));
              return;
            }
            established = false;
            if (!this.isManualDisconnect) {
              this.connectionState = CONNECTION_STATES.DISCONNECTED;
              this.notifyConnectionListeners();
              this.scheduleReconnect();
            }
          }
        });
        this.client = client;

        // Update connection state and start connecting
        this.connectionState = CONNECTION_STATES.CONNECTING;
        this.notifyConnectionListeners();
        client.activate();

      } catch (error) {
        console.error('❌ Failed to create WebSocket connection:', error);
        this.connectionState = CONNECTION_STATES.ERROR;
        this.notifyConnectionListeners();
        reject(error);
      }
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    this.isManualDisconnect = true;
    this.wantConnected = false;
    
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    if (this.client) {
      this.client.deactivate();
      this.client = null;
    }

    this.connectionState = CONNECTION_STATES.DISCONNECTED;
    this.subscriptions.clear();
    this.messageHandlers.clear();
    this.notifyConnectionListeners();
  }

  /**
   * Subscribe to a WebSocket destination
   * @param {string} destination - The destination to subscribe to (e.g., '/topic/conversations/123')
   * @param {function} messageHandler - Callback function to handle incoming messages
   * @param {Object} headers - Additional subscription headers
   * @returns {string} subscriptionId - Unique identifier for this subscription
   */
  subscribe(destination, messageHandler, headers = {}) {
    if (!this.client || !this.client.connected) {
      console.warn('⚠️ Cannot subscribe: WebSocket not connected');
      return null;
    }

    try {
      // Subscribe using STOMP client
      const subscription = this.client.subscribe(destination, (message) => {
        try {
          const parsedMessage = JSON.parse(message.body);
          messageHandler(parsedMessage, message);
        } catch (error) {
          console.error('❌ Error parsing WebSocket message:', error);
          messageHandler(message.body, message);
        }
      }, headers);

      // Store subscription for cleanup
      const subscriptionId = subscription.id;
      this.subscriptions.set(subscriptionId, subscription);

      // Store message handler for re-subscription after reconnect
      if (!this.messageHandlers.has(destination)) {
        this.messageHandlers.set(destination, new Set());
      }
      this.messageHandlers.get(destination).add({ handler: messageHandler, headers, subscriptionId });

      return subscriptionId;

    } catch (error) {
      console.error(`❌ Failed to subscribe to ${destination}:`, error);
      return null;
    }
  }

  /**
   * Send a message to a WebSocket destination
   * @param {string} destination - WebSocket destination to send to
   * @param {Object} body - Message body to send
   * @param {Object} headers - Optional headers for the message
   */
  send(destination, body = {}, headers = {}) {
    if (!this.client || !this.client.connected) {
      console.warn('⚠️ Cannot send message: WebSocket not connected');
      return false;
    }

    try {
      const messageBody = typeof body === 'string' ? body : JSON.stringify(body);
      this.client.publish({
        destination,
        body: messageBody,
        headers
      });
      return true;
    } catch (error) {
      console.error(`❌ Failed to send message to ${destination}:`, error);
      return false;
    }
  }

  /**
   * Unsubscribe from a WebSocket destination
   * @param {string} subscriptionId - The subscription ID returned from subscribe()
   */
  unsubscribe(subscriptionId) {
    if (!subscriptionId) return;

    try {
      const subscription = this.subscriptions.get(subscriptionId);
      if (subscription) {
        try {
          subscription.unsubscribe();
        } catch (error) {
          // The connection is already gone; the bookkeeping below must still run so no stale handler is restored
        }
        this.subscriptions.delete(subscriptionId);
        
        // Remove from message handlers
        for (const [destination, handlers] of this.messageHandlers.entries()) {
          const updatedHandlers = new Set();
          for (const handlerInfo of handlers) {
            if (handlerInfo.subscriptionId !== subscriptionId) {
              updatedHandlers.add(handlerInfo);
            }
          }
          if (updatedHandlers.size === 0) {
            this.messageHandlers.delete(destination);
          } else {
            this.messageHandlers.set(destination, updatedHandlers);
          }
        }
      }
    } catch (error) {
      console.error(`❌ Failed to unsubscribe ${subscriptionId}:`, error);
    }
  }

  /**
   * Add a connection state listener
   * @param {function} listener - Callback function that receives connection state changes
   * @returns {function} cleanup function to remove the listener
   */
  addConnectionListener(listener) {
    this.connectionListeners.add(listener);
    
    // Immediately notify with current state
    listener(this.connectionState);
    
    // Return cleanup function
    return () => {
      this.connectionListeners.delete(listener);
    };
  }

  /**
   * Get current connection state
   * @returns {string} current connection state
   */
  getConnectionState() {
    return this.connectionState;
  }

  /**
   * Check if WebSocket is currently connected
   * @returns {boolean} true if connected
   */
  isConnected() {
    return this.connectionState === CONNECTION_STATES.CONNECTED && 
           this.client && 
           this.client.connected;
  }

  // ==================== PRIVATE METHODS ====================

  /**
   * Notify all connection listeners of state change
   * @private
   */
  notifyConnectionListeners() {
    this.connectionListeners.forEach(listener => {
      try {
        listener(this.connectionState);
      } catch (error) {
        console.error('❌ Error in connection listener:', error);
      }
    });
  }

  /**
   * Schedule reconnection attempt with exponential backoff
   * @private
   */
  scheduleReconnect() {
    if (this.reconnectAttempts >= WEBSOCKET_CONFIG.MAX_RECONNECT_ATTEMPTS) {
      console.error('❌ Max reconnection attempts reached. Giving up.');
      this.connectionState = CONNECTION_STATES.ERROR;
      this.notifyConnectionListeners();
      return;
    }

    const delay = Math.min(
      WEBSOCKET_CONFIG.RECONNECT_DELAY * Math.pow(2, this.reconnectAttempts),
      30000 // Max 30 seconds
    );

    this.reconnectAttempts++;
    this.connectionState = CONNECTION_STATES.RECONNECTING;
    this.notifyConnectionListeners();

    this.reconnectTimeoutId = setTimeout(() => {
      this.reconnectTimeoutId = null;
      this.attemptReconnect();
    }, delay);
  }

  /**
   * Attempt to reconnect and restore subscriptions
   * @private
   */
  async attemptReconnect() {
    try {
      // Cookie-based auth (production) has no token to send
      const authToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || null;

      // Attempt to reconnect
      await this.connect(authToken);
      
      // Restore previous subscriptions
      this.restoreSubscriptions();

    } catch (error) {
      console.error('❌ Reconnection failed:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Restore all subscriptions after reconnection
   * @private
   */
  restoreSubscriptions() {
    const subscriptionsToRestore = new Map(this.messageHandlers);
    this.messageHandlers.clear();
    this.subscriptions.clear();

    for (const [destination, handlers] of subscriptionsToRestore) {
      for (const handlerInfo of handlers) {
        this.subscribe(destination, handlerInfo.handler, handlerInfo.headers);
      }
    }
  }

  /**
   * Get the appropriate WebSocket URL based on environment
   * @private
   */
  getWebSocketUrl() {
    // First priority: smart detection from window.location
    const { protocol, hostname, port } = window.location;
    
    // Development environment detection
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      // Local development: frontend is on 3000, notification service is on 8081
      return `${protocol}//${hostname}:8081/ws`;
    }

    // Production/staging: same domain as frontend
    const baseUrl = port ? `${protocol}//${hostname}:${port}` : `${protocol}//${hostname}`;
    
    // Second priority: explicit environment variable override
    if (process.env.REACT_APP_WS_URL) {
      return process.env.REACT_APP_WS_URL;
    }

    // Third priority: derive from API URL if available
    if (process.env.REACT_APP_API_URL) {
      // Remove '/api' suffix and add '/ws'
      const envBaseUrl = process.env.REACT_APP_API_URL.replace(/\/api$/, '');
      return `${envBaseUrl}/ws`;
    }

    // Default: use the detected base URL
    return `${baseUrl}/ws`;
  }
}

// Export singleton instance
const webSocketManager = new WebSocketManager();
export default webSocketManager;